import { componentThemes } from '@theme/componentThemes';
import { parseValue } from '@theme/themeUtils';
import React from 'react';
import {
  TouchableOpacity,
  TouchableOpacityProps,
  ViewStyle,
  TextStyle,
  ActivityIndicator,
  Platform,
  View,
} from 'react-native';
import Text from './Text';

// Button variant type
type ButtonVariant = 'solid' | 'ghost' | 'group';

// Button size type
type ButtonSize = 'sm' | 'md' | 'lg';

// Extended Button props interface
interface ButtonProps extends Omit<TouchableOpacityProps, 'style'> {
  // Button specific props
  variant?: ButtonVariant;
  size?: ButtonSize;

  // Content
  children?: React.ReactNode;

  // Loading state
  isLoading?: boolean;
  loadingText?: string;

  // Disabled state
  isDisabled?: boolean;

  // Styling props
  color?: string;
  backgroundColor?: string;
  borderColor?: string;

  // Spacing props
  marginTop?: number | string;
  marginBottom?: number | string;
  marginLeft?: number | string;
  marginRight?: number | string;
  marginX?: number | string;
  marginY?: number | string;
  paddingTop?: number | string;
  paddingBottom?: number | string;
  paddingLeft?: number | string;
  paddingRight?: number | string;
  paddingX?: number | string;
  paddingY?: number | string;

  // Layout props
  flex?: number;
  width?: number | string;
  height?: number | string;
  minWidth?: number | string;
  minHeight?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;
  alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';

  // Border props
  borderRadius?: number | string;
  borderWidth?: number | string;

  // Text props (for button text styling)
  textColor?: string;
  textVariant?: string;
  textSize?: string;
  textAlign?: 'auto' | 'left' | 'right' | 'center' | 'justify';
  textTransform?: 'none' | 'capitalize' | 'uppercase' | 'lowercase';

  // Icon props
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;

  // NativeBase _text prop support
  _text?: {
    testID?: string;
    color?: string;
    size?: string;
    fontWeight?: string;
    textTransform?: 'none' | 'capitalize' | 'uppercase' | 'lowercase';
  };

  // Custom styles
  style?: ViewStyle;
  textStyle?: TextStyle;

  // NativeBase theme variants (we'll ignore these for now)
  _dark?: any;
  _light?: any;
  _hover?: any;
  _focus?: any;
  _pressed?: any;
  _disabled?: any;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'solid',
  size = 'md',
  children,
  isLoading = false,
  loadingText,
  isDisabled = false,
  color,
  backgroundColor,
  borderColor,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  marginX,
  marginY,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  paddingX,
  paddingY,
  flex,
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  alignSelf,
  borderRadius,
  borderWidth,
  textColor,
  textVariant,
  textSize,
  textAlign,
  textTransform,
  leftIcon,
  rightIcon,
  _text,
  style,
  textStyle,
  onPress,
  // Ignore theme variants
  _dark,
  _light,
  _hover,
  _focus,
  _pressed,
  _disabled,
  ...rest
}) => {
  const buttonTheme = componentThemes.button;

  // Get button variant theme
  const getVariantTheme = () => {
    switch (variant) {
      case 'solid':
        return buttonTheme.variants.solid;
      case 'ghost':
        return buttonTheme.variants.ghost;
      case 'group':
        return buttonTheme.variants.group;
      default:
        return buttonTheme.variants.solid;
    }
  };

  // Get button size theme
  const getSizeTheme = () => buttonTheme.sizes[size];

  const variantTheme = getVariantTheme();
  const sizeTheme = getSizeTheme();

  // Determine if button is disabled
  const disabled = isDisabled || isLoading;

  // Get button styles based on state
  const getButtonStyle = (): ViewStyle => {
    // Start with variant base styles
    const baseStyle = {
      ...variantTheme.base,
    };

    // Apply size theme (this should override variant padding)
    const sizeAdjustedStyle = {
      ...baseStyle,
      ...sizeTheme,
    };

    // Override with custom props
    const customStyle: ViewStyle = {
      ...sizeAdjustedStyle,
      // Spacing - custom props take precedence
      marginTop: parseValue(marginTop),
      marginBottom: parseValue(marginBottom),
      marginLeft: parseValue(marginLeft),
      marginRight: parseValue(marginRight),
      marginHorizontal: parseValue(marginX),
      marginVertical: parseValue(marginY),
      paddingTop: parseValue(paddingTop),
      paddingBottom: parseValue(paddingBottom),
      paddingLeft: parseValue(paddingLeft),
      paddingRight: parseValue(paddingRight),
      paddingHorizontal: parseValue(paddingX),
      paddingVertical: parseValue(paddingY),

      // Layout
      flex,
      width: parseValue(width),
      height: parseValue(height),
      minWidth: parseValue(minWidth),
      minHeight: parseValue(minHeight),
      maxWidth: parseValue(maxWidth),
      maxHeight: parseValue(maxHeight),
      alignSelf,

      // Border
      borderRadius: parseValue(borderRadius) || variantTheme.base.borderRadius,
      borderWidth: parseValue(borderWidth) || variantTheme.base.borderWidth,
      borderColor: borderColor || variantTheme.base.borderColor,

      // Background
      backgroundColor: backgroundColor || variantTheme.base.backgroundColor,

      // Web-specific enhancements
      ...(Platform.OS === 'web' && {
        cursor: disabled ? 'not-allowed' : 'pointer',
        userSelect: 'none',
        outline: 'none',
      }),
    };

    // Apply disabled styles
    if (disabled) {
      return {
        ...customStyle,
        ...variantTheme.disabled,
        opacity: 0.9,
      };
    }

    return customStyle;
  };

  // Get text styles
  const getTextStyle = (): TextStyle => {
    const baseTextStyle = variantTheme.text;

    // Handle _text prop (NativeBase compatibility)
    const textProps = _text || {};

    const customTextStyle: TextStyle = {
      ...baseTextStyle,
      color: textColor || textProps.color || color || baseTextStyle.color,
      textAlign: textAlign || 'center',
      textTransform: textTransform || textProps.textTransform || 'none',
    };

    // Apply disabled text styles
    if (disabled) {
      return {
        ...customTextStyle,
        ...variantTheme.disabledText,
      };
    }

    return customTextStyle;
  };

  // Handle press events
  const handlePress = (event: any) => {
    if (!disabled && onPress) {
      onPress(event);
    }
  };

  // Render button content
  const renderContent = () => {
    if (isLoading) {
      return <ActivityIndicator testID="loading-indicator" size="small" color={getTextStyle().color as string} />;
    }

    // Create content array to handle icons and text
    const content = [];

    // Add left icon if provided
    if (leftIcon) {
      content.push(<React.Fragment key="leftIcon">{leftIcon}</React.Fragment>);
    }

    // Add text content
    if (typeof children === 'string') {
      content.push(
        <Text
          key="text"
          testID={_text?.testID}
          variant={(textVariant as any) || 'md-normal'}
          size={(textSize as any) || (_text?.size as any)}
          color={getTextStyle().color as string}
          style={[textStyle, { textTransform: getTextStyle().textTransform }]}>
          {children}
        </Text>,
      );
    } else if (children) {
      content.push(<React.Fragment key="children">{children}</React.Fragment>);
    }

    // Add right icon if provided
    if (rightIcon) {
      content.push(<React.Fragment key="rightIcon">{rightIcon}</React.Fragment>);
    }

    // If we have multiple items, wrap in a View with flexDirection row
    if (content.length > 1) {
      return (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8, // Add some spacing between icons and text
          }}>
          {content}
        </View>
      );
    }

    // Return single item
    return content[0] || null;
  };

  return (
    <TouchableOpacity
      style={[getButtonStyle(), style]}
      onPress={handlePress}
      disabled={disabled}
      activeOpacity={disabled ? 1 : 0.7}
      {...rest}
      accessibilityState={disabled ? { disabled: true } : undefined}>
      {renderContent()}
    </TouchableOpacity>
  );
};

export default Button;
